import { CANNED_SCENARIOS, cannedById, EXAMPLE_WHITEBOARD_ID } from './canned'
import { BLANK_BOARD_ID } from './canned/blankBoard'
import type { ScenarioTemplate } from './types'

export function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Next unused title in a copy series: `Foo`, `Foo (2)`, `Foo (3)`, …
 * Cloning `Foo (5)` continues the `Foo` series → `Foo (6)`, not `Foo (5) (2)`.
 * Pass only titles that should collide (e.g. the viewer’s own sessions) — not Oversight.
 */
export function nextCopyTitle(
  baseTitle: string,
  instances: readonly { session: { title: string } }[],
): string {
  const trimmed = baseTitle.trim()
  const peel = trimmed.match(/^(.*) \((\d+)\)$/)
  const stem = ((peel ? peel[1]! : trimmed) || trimmed).trim()
  const re = new RegExp(`^${escapeRegExp(stem)} \\((\\d+)\\)$`)
  let seenBare = false
  let maxN = 0
  for (const inst of instances) {
    const title = inst.session.title.trim()
    if (title === stem) seenBare = true
    const match = title.match(re)
    if (match) maxN = Math.max(maxN, Number(match[1]))
  }
  // Count the source’s own (n) even if the list is briefly stale.
  if (peel) maxN = Math.max(maxN, Number(peel[2]))
  if (!seenBare && maxN === 0) return stem
  return `${stem} (${Math.max(maxN, seenBare ? 1 : 0) + 1})`
}

/** Build a template record from a canned factory (library preview fallback). */
export function templateFromCanned(id: string): ScenarioTemplate {
  const info = cannedById(id)
  const session = info.create()
  return {
    id: info.id,
    title: info.title,
    summary: info.summary,
    durationMs: info.durationMs,
    mapLabel: info.mapLabel,
    hiddenFromTemplates: info.hiddenFromTemplates,
    session,
    updatedAtIso: new Date().toISOString(),
  }
}

function isExampleWhiteboardTemplate(t: ScenarioTemplate): boolean {
  return t.id === EXAMPLE_WHITEBOARD_ID || t.session.id === EXAMPLE_WHITEBOARD_ID
}

export function visibleTemplates(templates: readonly ScenarioTemplate[]): ScenarioTemplate[] {
  const rank = new Map(CANNED_SCENARIOS.map((row, index) => [row.id, index]))
  return templates
    .filter((t) => !t.hiddenFromTemplates && !isExampleWhiteboardTemplate(t))
    .slice()
    .sort((a, b) => {
      const ar = rank.get(a.session?.id ?? a.id) ?? rank.get(a.id) ?? 1000
      const br = rank.get(b.session?.id ?? b.id) ?? rank.get(b.id) ?? 1000
      return ar - br || a.title.localeCompare(b.title)
    })
}

export function templateById(
  templates: readonly ScenarioTemplate[],
  id: string,
): ScenarioTemplate {
  return templates.find((t) => t.id === id) ?? templateFromCanned(BLANK_BOARD_ID)
}

/** Library landing default — blank whiteboard when present, else first visible. */
export function defaultTemplateId(templates: readonly ScenarioTemplate[]): string {
  const vis = visibleTemplates(templates)
  const blank = vis.find((t) => t.id === BLANK_BOARD_ID || t.session.id === BLANK_BOARD_ID)
  return blank?.id ?? vis[0]?.id ?? BLANK_BOARD_ID
}
